import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, FileText, Search, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import { useRBAC } from "@/hooks/useRBAC";
import ConfirmModal from "@/components/common/ConfirmModal";
import type { DocumentListResponse } from "@/types/document";
import { CONFIDENTIALITY_COLORS, STATUS_COLORS } from "@/types/document";

type SortOrder = "asc" | "desc";

export default function DocumentListPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const { canCreate, canDelete } = useRBAC();
  const queryClient = useQueryClient();

  // Document targeted for deletion (drives the confirm modal). null = closed.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; judul: string } | null>(null);
  const [deleteError, setDeleteError] = useState("");

  const { data, isLoading } = useQuery<DocumentListResponse>({
    queryKey: ["documents", page, pageSize, search, sortOrder],
    queryFn: () =>
      api.get("/documents/", {
        page,
        page_size: pageSize,
        sort_order: sortOrder,
        ...(search ? { search } : {}),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      setDeleteTarget(null);
    },
    onError: () =>
      setDeleteError("Gagal menghapus dokumen. Silakan coba lagi."),
  });

  function toggleSort() {
    setPage(1);
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  }

  // Total columns — used for the empty-state colspan (grows when actions show).
  // Kolom Aksi dinonaktifkan sementara, jadi selalu 6. (sebelumnya: canDelete ? 7 : 6)
  const colCount = 6;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daftar Dokumen</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {data?.total ?? 0} dokumen ditemukan
          </p>
        </div>
        {canCreate && (
          <Link
            to="/documents/upload"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Tambah Dokumen</span>
          </Link>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Cari dokumen..."
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Judul</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Klasifikasi PNBP</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">Stakeholder</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Kerahasiaan</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                  <button
                    onClick={toggleSort}
                    className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                  >
                    Dibuat
                    {sortOrder === "desc" ? (
                      <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUp className="h-3 w-3" />
                    )}
                  </button>
                </th>
                {/* Aksi Hapus dinonaktifkan sementara — disimpan untuk dipakai lagi nanti.
                {canDelete && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Aksi
                  </th>
                )}
                */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.items ?? []).map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 max-w-0 w-full">
                    <Link
                      to={`/documents/${doc.id}`}
                      title={doc.judul}
                      className="flex items-center gap-2 font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{doc.judul}</span>
                    </Link>
                    {doc.nomor_dokumen && (
                      <p title={doc.nomor_dokumen} className="text-xs text-gray-400 mt-0.5 pl-6 truncate">{doc.nomor_dokumen}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell whitespace-nowrap">
                    {doc.taxonomy_node_nama || doc.jenis_pnbp_nama}
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    {doc.stakeholders.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {doc.stakeholders.slice(0, 2).map((s) => (
                          <span key={s.id} className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 whitespace-nowrap">
                            {s.nama}
                          </span>
                        ))}
                        {doc.stakeholders.length > 2 && (
                          <span className="text-xs text-gray-400">+{doc.stakeholders.length - 2}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[doc.status_kode]}`}>
                      {doc.status_nama}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${CONFIDENTIALITY_COLORS[doc.tingkat_kerahasiaan]}`}>
                      {doc.tingkat_kerahasiaan}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell whitespace-nowrap">
                    {new Date(doc.created_at).toLocaleDateString("id-ID")}
                    {" "}
                    <span className="text-gray-400">
                      {new Date(doc.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <br />
                    <span className="text-gray-400">{doc.created_by_username}</span>
                  </td>
                  {/* Aksi Hapus dinonaktifkan sementara — disimpan untuk dipakai lagi nanti.
                  {canDelete && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => {
                          setDeleteError("");
                          setDeleteTarget({ id: doc.id, judul: doc.judul });
                        }}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        title="Hapus dokumen"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Hapus</span>
                      </button>
                    </td>
                  )}
                  */}
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Tidak ada dokumen ditemukan
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-500">
          <span>Tampilkan</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>per halaman</span>
          {data && (
            <span className="ml-1 hidden sm:inline">· {data.total} dokumen</span>
          )}
        </div>

        {data && data.total_pages > 1 && (
          <div className="flex items-center gap-2">
            <p className="text-gray-500">
              Halaman {page} dari {data.total_pages}
            </p>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
            >
              Sebelumnya
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              disabled={page === data.total_pages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
            >
              Berikutnya
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmModal
        open={deleteTarget !== null}
        variant="danger"
        title="Hapus Dokumen"
        message={
          deleteError ||
          `Yakin ingin menghapus "${deleteTarget?.judul ?? ""}"? Dokumen akan dihapus dari daftar.`
        }
        confirmLabel="Hapus"
        cancelLabel="Batal"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError("");
        }}
      />
    </div>
  );
}
